import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { ProductionZonesActions } from "@/components/common/section-components/action/ProductionZonesActions";
import { ProductionZonesFilter } from "@/components/common/section-components/filters/ProductionZonesFilter";
import { NoData } from "@/components/common/section-components/NoData";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { Sucursal, getSucursales } from "@/services/accounts.service";
import { GetProductionZonesListProps } from "@/services/production-zones.service";
import { isEmpty } from "@/utils/query-string";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  clearFiltersHandler,
  fetchProductionZonesHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { productionZonesHeaderElements } from "./schema/header-elements";
import {
  ProductionZoneListEntity,
  ProductionZonesListResponse,
  productionZonesFilterSchema,
} from "./schema/production-zones.schema";

export const ProductionZonesPage = () => {
  const navigate = useNavigate();

  const [isActive, setIsActive] = useState(false);
  const [loadingProductionZones, setLoadingProductionZones] = useState(false);
  const [productionZones, setProductionZones] =
    useState<ProductionZonesListResponse | null>(null);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [page, setPage] = useState(1);
  const [openFilters, setOpenFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<
    Partial<GetProductionZonesListProps> | undefined
  >();

  const { fetchPermissions, hasWriteAccess } = useRoles();

  const { control, setValue, getValues, handleSubmit } = useForm({
    resolver: zodResolver(productionZonesFilterSchema),
    defaultValues: {
      Nombre: "",
      ZonaComercial: [],
      Activo: [],
    },
  });

  useEffect(() => {
    setIsActive(true);
  }, []);

  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate =
    hasSelection &&
    selection.length < (productionZones?.listado?.length ?? 0);

  const fetchSucursales = async () => {
    setLoadingSucursales(true);
    const response = await getSucursales();
    setSucursales((response ?? []) as Sucursal[]);
    setLoadingSucursales(false);
  };

  const fetchProductionZones = async (
    filters?: Partial<GetProductionZonesListProps>,
    showLoad = true,
  ) => {
    await fetchProductionZonesHandler({
      setLoadingProductionZones,
      setProductionZones,
      navigate,
      fetchPermissions,
      showLoad,
      filters,
    });
  };

  const submitFilters = async () => {
    await submitFiltersHandler({
      event: getValues(),
      setOpenFilters,
      setFiltersApplied,
      setCurrentFilters,
      setPage,
      fetchProductionZones,
    });
    setSelection([]);
  };

  const clearFilters = async () => {
    await clearFiltersHandler({
      setValue,
      setFiltersApplied,
      getValues,
      setOpenFilters,
      submitFilters: async () =>
        submitFiltersHandler({
          event: getValues(),
          setOpenFilters,
          setFiltersApplied,
          setCurrentFilters,
          setPage,
          fetchProductionZones,
        }),
    });
    setSelection([]);
  };

  useEffect(() => {
    if (isActive) {
      fetchProductionZones();
      fetchSucursales();
    }
  }, [isActive]);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    setSelection([]);
    fetchProductionZones({ ...currentFilters, "P.Pagina": nextPage }, true);
  };

  const handleDetail = (id: number) => {
    navigate(AppRoutes.PRODUCTION_ZONES_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: ProductionZoneListEntity) => (
    <ProductionZonesActions handleDetail={handleDetail} item={item} />
  );

  const values = useWatch({ control });
  const filtersEmpty = Object.values(values ?? {}).every((v) => isEmpty(v));

  const items =
    productionZones?.listado?.map((el: ProductionZoneListEntity) => ({
      ...el,
      activo: el.activo ? "Activa" : "Inactiva",
    })) ?? [];

  const handleAddProductionZone = () => {
    navigate(AppRoutes.PRODUCTION_ZONES_CREATION);
  };

  return (
    <Flex flexDir="column" paddingTop="20px" data-testid="production-zones-page">
      <SectionHeader
        title={"Zonas Productivas"}
        addButtonLabel="Agregar"
        handleAdd={handleAddProductionZone}
        loading={loadingProductionZones}
        showButtons={hasWriteAccess(PermissionCode.PRODUCTION_ZONES)}
      />

      <ProductionZonesFilter
        control={control}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        sucursales={sucursales}
        disableFilters={loadingProductionZones}
        loadingFilters={loadingProductionZones}
        loadingSucursales={loadingSucursales}
        filtersApplied={filtersApplied}
        disableClear={loadingProductionZones}
        disableApply={loadingProductionZones || filtersEmpty}
      />

      <Flex flexDir="column">
        {Boolean(!loadingProductionZones && !productionZones?.listado?.length) && (
          <NoData />
        )}

        {Boolean(!loadingProductionZones && productionZones?.listado?.length) && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={indeterminate}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Editar"
              items={items}
              headers={productionZonesHeaderElements}
              actionElements={getActionElements}
              noActions={!hasWriteAccess(PermissionCode.PRODUCTION_ZONES)}
              noSelection
            />

            {Boolean(productionZones?.totalPages && productionZones.totalPages > 1) && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={productionZones?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
